import type { ApolloQueryResult } from 'apollo-client';

import type { DwellTimeHelperData } from '../analytics/DwellTimeHelper';

import type {
	ContentState,
	ContentMode,
	ContentType,
	ConsumptionCompletedEvent,
	ConsumptionInitiatedEvent,
	ConsumptionViewedEvent,
	ConsumptionErrorEvent,
	ConsumptionEventRequiredData,
	ConsumptionInitiatedEventData,
} from './ConsumptionEvents';
import type { getConsumptionEventDataQuery } from './__types__/getConsumptionEventDataQuery.js';

type unknownString = string | null | undefined;

export const mapTypeToContentState = (status: unknownString): ContentState => {
	switch (status) {
		case 'current':
			return 'published';
		case 'archived':
			return 'archived';
		case 'draft':
			return 'draft';
		default:
			return 'unset';
	}
};

export const mapSubTypeToContentMode = (subType: unknownString): ContentMode => {
	switch (subType) {
		case 'live':
			return 'live';
		case 'embed':
			return 'embed';
		default:
			return 'classic';
	}
};

export const mapTypeToContentType = (type: unknownString): ContentType => {
	switch (type) {
		case 'page':
			return 'page';
		case 'blogpost':
			return 'blogpost';
		case 'whiteboard':
			return 'whiteboard';
		case 'database':
			return 'database';
		case 'smartlink':
			return 'smartlink';
		default:
			return 'unset';
	}
};

export const createConsumptionViewedEvent = (
	response: ApolloQueryResult<getConsumptionEventDataQuery>,
	data: ConsumptionEventRequiredData,
): ConsumptionViewedEvent => {
	const contentType = mapTypeToContentType(response?.data?.singleContent?.type);
	const contentState = mapTypeToContentState(response?.data?.singleContent?.status);
	const contentMode = mapSubTypeToContentMode(response?.data?.singleContent?.subType);

	return {
		type: 'sendTrackEvent',
		data: {
			action: 'viewed',
			actionSubject: 'consumption',
			actionSubjectId: 'client',
			source: data.source,
			attributes: {
				contentType,
				contentMode,
				contentState,
				spaceId: response?.data.space?.id ?? 'unset',
				contentId: response?.data.singleContent?.id ?? 'unset',
			},
		},
	};
};

export const createConsumptionInitiatedEvent = (
	response: ApolloQueryResult<getConsumptionEventDataQuery>,
	data: ConsumptionInitiatedEventData,
): ConsumptionInitiatedEvent => {
	const contentType = mapTypeToContentType(response?.data?.singleContent?.type);
	const contentState = mapTypeToContentState(response?.data?.singleContent?.status);
	const contentMode = mapSubTypeToContentMode(response?.data?.singleContent?.subType);

	return {
		type: 'sendTrackEvent',
		data: {
			action: 'initiated',
			actionSubject: 'consumption',
			actionSubjectId: 'client',
			source: data.source,
			attributes: {
				contentType,
				contentMode,
				contentState,
				spaceId: response?.data.space?.id ?? 'unset',
				contentId: response?.data.singleContent?.id ?? 'unset',
				utmSource: data.utmSource,
			},
		},
	};
};

export const createConsumptionCompletedEvent = (
	event: ConsumptionViewedEvent,
	dwellTimeHelperData: DwellTimeHelperData,
): ConsumptionCompletedEvent => {
	return {
		type: 'sendTrackEvent',
		data: {
			...event.data,
			action: 'completed',
			attributes: {
				...event.data.attributes,
				dwellTime: dwellTimeHelperData.attributes.dwellTime,
			},
		},
	};
};

export const createConsumptionErrorEvent = (
	errorMessage: string,
	props: ConsumptionEventRequiredData,
): ConsumptionErrorEvent => {
	return {
		type: 'sendTrackEvent',
		data: {
			action: 'error',
			actionSubject: 'consumption',
			actionSubjectId: 'client',
			source: props.source,
			attributes: {
				error: errorMessage,
				contentId: props.contentId ?? 'unset',
				spaceKey: props.spaceKey,
			},
		},
	};
};
